import React from 'react';
import {
  Button,
  Flex,
  Link,
  Color,
  Icon,
  IconEnum,
  Text,
} from 'component-library';
import { makeStyles } from '@material-ui/core';
import { useHistoryStore } from 'stores/useStores';

const useStyles = makeStyles(() => ({
  ecStepContainer: {
    gridGap: 4,
    gap: 4,
    alignItems: 'flex-start',
  },
  iconContainer: {
    paddingRight: '8px;,',
  },
  link: {
    display: 'flex',
    alignItems: 'end',
    textDecoration: 'none',
  },
  linkText: {
    color: Color.blue._60,
  },
  circleIcon: {
    paddingLeft: '10px',
    paddingTop: '10px',
    width: '39px',
    height: '39px',
    marginLeft: '-8px',
    marginRight: '-8px',
    marginTop: '-6px',
  },
  active: {
    background: Color.neutral._light_20,
    border: '1px solid',
    borderColor: Color.neutral._20,
    borderRadius: '8px',
  },
  completed: {
    color: Color.neutral._60,
  },
  upcoming: {
    color: Color.neutral._40,
  },
  callout: {
    background: Color.blue._20,
  },
}));

interface ExpenseClassificationSubStepProps {
  title: string;
  text: string;
  actionText: string;
  link: string;
  status: 'active' | 'completed' | 'upcoming';
  dataTestId: string;
  callout?: boolean;
}

export const ExpenseClassificationSubStep = ({
  title,
  text,
  actionText,
  link,
  status,
  dataTestId,
  callout,
}: ExpenseClassificationSubStepProps) => {
  const classes = useStyles();
  const history = useHistoryStore();
  const active = status === 'active';

  return (
    <Flex
      className={`${classes.ecStepContainer} ${active ? classes.active : ''}`}
      direction='column'
    >
      <Flex padding={[8, 16]}>
        <div className={classes.iconContainer}>
          {(active || status === 'upcoming') && (
            <Icon
              size={24}
              name={IconEnum.circle}
              className={`${classes.circleIcon} ${
                status === 'upcoming' ? classes.upcoming : ''
              }`}
              color={status === 'upcoming' ? Color.neutral._40 : undefined}
            />
          )}
          {status === 'completed' && (
            <Icon
              size={24}
              name={IconEnum.check_circle_filled}
              color={Color.semantic.$success50}
            />
          )}
        </div>
        <Flex alignItems='center' justifyContent='space-between'>
          <div>
            <Flex alignItems='center' justifyContent='start' gap={4}>
              <Text tag='p' variant='medium' text={title} />
              {active && actionText && (
                <Text tag='p' size={13} color={Color.purple._70}>
                  &bull; {actionText}
                </Text>
              )}
            </Flex>
            {status !== 'completed' && (
              <Text
                size={13}
                tag='p'
                variant='regular'
                color={Color.neutral._80}
              >
                {text}
              </Text>
            )}
          </div>
          {active && (
            <div>
              <Button
                label='Continue'
                onClick={() => history.push(link)}
                small
                dataTestId={`${dataTestId}-button`}
              />
            </div>
          )}
        </Flex>
        {status === 'completed' && (
          <Flex alignItems='center' justifyContent='end'>
            <Icon size={16} name={IconEnum.pen} color={Color.blue._60} />
            <Link
              dataTestId={`${dataTestId}-edit-link`}
              variant='medium'
              text='Edit'
              href={`${link}?edit=true`}
              size={15}
            />
          </Flex>
        )}
      </Flex>
      {active && callout && (
        <Flex
          className={classes.callout}
          alignItems='center'
          justifyContent='start'
          gap={4}
          padding={[8, 16]}
        >
          <Icon size={24} name={IconEnum.info_circle} color={Color.blue._70} />
          <Text
            size={13}
            tag='p'
            variant='regular'
            text={`The more time your employees spent on R&D activities, the more credit you'll receive!`}
          />
        </Flex>
      )}
    </Flex>
  );
};
